import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  HeaderCell,
  HeaderControlCell,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getFaceToFaceEncounterForm,
  updateFaceToFaceEncounterForm,
} from "../../../../../services/patientService";
import {
  CalendarIcon,
  CheckCircleIcon,
  DoctorIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HomeIcon,
  MapPinIcon,
  MedicalNotesIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import {
  buildInsurances,
  buildPhysicians,
} from "../../../patientForm.shared";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  CMS_TIMEFRAME_OPTIONS,
  CREDENTIAL_OPTIONS,
  ELIGIBILITY_CHECK_OPTIONS,
  ENCOUNTER_METHOD_OPTIONS,
  ENCOUNTER_RELATED_OPTIONS,
  ENCOUNTER_TIMING_OPTIONS,
  F2F_ARRAY_KEYS,
  HOMEBOUND_CRITERION1_OPTIONS,
  HOMEBOUND_DETERMINATION_OPTIONS,
  PLACE_OF_ENCOUNTER_OPTIONS,
  PRACTITIONER_ROLE_OPTIONS,
  QA_CHECKLIST_OPTIONS,
  QA_STATUS_OPTIONS,
  REQUESTED_SERVICE_KEYS,
  SKILLED_DISCIPLINE_OPTIONS,
  SKILLED_NEED_OPTIONS,
  SYMPTOM_OPTIONS,
  TELEHEALTH_VERIFIED_OPTIONS,
  US_STATE_OPTIONS,
  YES_NO_OPTIONS,
  emptyDiagnosisRow,
  emptyFaceToFacePayload,
  emptyQaChecklist,
  mapChartDiagnosesToF2fReason,
  otherDiagnosesAreEmpty,
  today,
  toggleInList,
} from "./faceToFaceContent";

const ENCOUNTER_DATE_MAX = new Date().toISOString().slice(0, 10);

const caregiverLabel = (form) => {
  const name = form?.filledByName || "";
  const filledRole = form?.filledByRole || "";
  if (name && filledRole) return `${name} (${filledRole})`;
  return name || filledRole || "—";
};

const emptyValues = () => ({
  ...emptyFaceToFacePayload(),
  clinicianSignatureName: "",
  reviewComments: "",
});

const formatPatientDisplay = (patient) => {
  const name = [patient?.lastName, patient?.firstName].filter(Boolean).join(", ");
  if (!name) return patient?.mrn ? `(${patient.mrn})` : "";
  return patient?.mrn ? `${name} (${patient.mrn})` : name;
};

const absoluteDayDiff = (dateA, dateB) => {
  const a = formatDateForInput(dateA);
  const b = formatDateForInput(dateB);
  if (!a || !b) return "";
  const d1 = new Date(`${a}T12:00:00`);
  const d2 = new Date(`${b}T12:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return "";
  return String(Math.abs(Math.round((d2.getTime() - d1.getTime()) / 86400000)));
};

const normalizeDiagnoses = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const count = Math.max(4, list.length);
  return Array.from({ length: count }, (_, i) => {
    const row = list[i] || {};
    return {
      id: row.id || emptyDiagnosisRow(`dx-${i + 1}`).id,
      diagnosis: row.diagnosis || "",
      icd10: row.icd10 || "",
      relevantToHh: row.relevantToHh || "",
    };
  });
};

const normalizeRequestedServices = (src) => {
  const incoming = src && typeof src === "object" ? src : {};
  return Object.fromEntries(
    REQUESTED_SERVICE_KEYS.map(({ key }) => [
      key,
      {
        selected: incoming[key]?.selected || "",
        reason: incoming[key]?.reason || "",
      },
    ]),
  );
};

const normalizeQaChecklist = (src) => ({
  ...emptyQaChecklist(),
  ...(src && typeof src === "object" ? src : {}),
});

const DateFieldInput = ({
  name,
  value,
  canEdit,
  printMode,
  onChange,
  max,
}) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      compact
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const FaceToFaceEncounterForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const prevEncounterSocKey = useRef(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyFaceToFacePayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const next = {
        ...base,
        ...loadedPayload,
        encounterDate:
          loadedPayload.encounterDate ||
          data.orderDate ||
          today(),
        otherDiagnoses: normalizeDiagnoses(loadedPayload.otherDiagnoses),
        requestedServices: normalizeRequestedServices(
          loadedPayload.requestedServices,
        ),
        qaChecklist: normalizeQaChecklist(loadedPayload.qaChecklist),
        certifyingDifferent: Boolean(loadedPayload.certifyingDifferent),
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      for (const key of F2F_ARRAY_KEYS) {
        next[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
      }

      setValues(next);
      prevEncounterSocKey.current = null;
      resetSignatures();
    },
    [resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const insurers = buildInsurances(patient);
    const physicians = buildPhysicians(patient);
    const primaryIns = insurers[0];
    const secondaryIns = insurers[1];
    const primaryPhys = physicians[0];
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc =
      latestEpisode?.socDate || latestEpisode?.startDate || "";
    const certTo = latestEpisode?.endDate || latestEpisode?.toDate || "";
    const agencyName =
      patient?.companyName ||
      patient?.agencyName ||
      patient?.homeHealthAgency ||
      "";

    const chartDiagnoses = mapChartDiagnosesToF2fReason(patient);
    const chartPrefill = {
      patientName: formatPatientDisplay(patient) || patientName || "",
      dateOfBirth: patient?.dateOfBirth
        ? formatDateForInput(patient.dateOfBirth)
        : "",
      mrn: patient?.mrn || "",
      address: patient?.address || "",
      city: patient?.city || "",
      state: patient?.state || "",
      zip: patient?.zip || "",
      phone: patient?.phone || "",
      primaryInsurance: primaryIns?.insuranceName || "",
      secondaryInsurance: secondaryIns?.insuranceName || "",
      homeHealthAgency: agencyName,
      agencyNpi: patient?.agencyNpi || "",
      agencyCcn: patient?.agencyCcn || "",
      socDate: soc ? formatDateForInput(soc) : "",
      certPeriodFrom: soc ? formatDateForInput(soc) : "",
      certPeriodTo: certTo ? formatDateForInput(certTo) : "",
      encounterDate: today(),
      practitionerName: primaryPhys?.physicianName || "",
      practitionerNpi: primaryPhys?.npiNumber || "",
      practitionerPhone: primaryPhys?.physicianPhone || "",
      practitionerFax: primaryPhys?.physicianFax || "",
      organization: primaryPhys?.physicianAddress || "",
      primaryCondition: chartDiagnoses.primaryCondition,
      primaryIcd10: chartDiagnoses.primaryIcd10,
      otherDiagnoses: chartDiagnoses.otherDiagnoses,
    };

    if (isDraft) {
      hydrate({
        formName:
          clinicalForm.formName ||
          draft?.formName ||
          "Face-to-Face Encounter",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        patientDateOfBirth: patient?.dateOfBirth || null,
        ...draftCaregiverIdentity(currentUser, role),
        createdAt: new Date().toISOString(),
        permissions: buildClinicalFormPermissions("pending", role),
        orderDate: today(),
        clinicianSignatureName: "",
        reviewComments: "",
        payload: {
          ...emptyFaceToFacePayload(),
          ...chartPrefill,
        },
      });
      if (!cancelled) {
        setLoading(false);
        onReady?.();
      }
      return () => {
        cancelled = true;
      };
    }

    getFaceToFaceEncounterForm(patientId, formId)
      .then((res) => {
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            patientName:
              existingPayload.patientName ||
              chartPrefill.patientName ||
              loaded.patientName ||
              "",
            dateOfBirth:
              existingPayload.dateOfBirth ||
              chartPrefill.dateOfBirth ||
              (loaded.patientDateOfBirth
                ? formatDateForInput(loaded.patientDateOfBirth)
                : "") ||
              "",
            mrn: existingPayload.mrn || chartPrefill.mrn || "",
            address: existingPayload.address || chartPrefill.address || "",
            city: existingPayload.city || chartPrefill.city || "",
            state: existingPayload.state || chartPrefill.state || "",
            zip: existingPayload.zip || chartPrefill.zip || "",
            phone: existingPayload.phone || chartPrefill.phone || "",
            primaryInsurance:
              existingPayload.primaryInsurance ||
              chartPrefill.primaryInsurance ||
              "",
            secondaryInsurance:
              existingPayload.secondaryInsurance ||
              chartPrefill.secondaryInsurance ||
              "",
            homeHealthAgency:
              existingPayload.homeHealthAgency ||
              chartPrefill.homeHealthAgency ||
              "",
            agencyNpi: existingPayload.agencyNpi || chartPrefill.agencyNpi || "",
            agencyCcn: existingPayload.agencyCcn || chartPrefill.agencyCcn || "",
            socDate: existingPayload.socDate || chartPrefill.socDate || "",
            certPeriodFrom:
              existingPayload.certPeriodFrom || chartPrefill.certPeriodFrom || "",
            certPeriodTo:
              existingPayload.certPeriodTo || chartPrefill.certPeriodTo || "",
            encounterDate:
              existingPayload.encounterDate ||
              loaded.orderDate ||
              today(),
            practitionerName:
              existingPayload.practitionerName ||
              chartPrefill.practitionerName ||
              "",
            practitionerNpi:
              existingPayload.practitionerNpi ||
              chartPrefill.practitionerNpi ||
              "",
            practitionerPhone:
              existingPayload.practitionerPhone ||
              chartPrefill.practitionerPhone ||
              "",
            practitionerFax:
              existingPayload.practitionerFax ||
              chartPrefill.practitionerFax ||
              "",
            organization:
              existingPayload.organization || chartPrefill.organization || "",
            primaryCondition:
              existingPayload.primaryCondition ||
              chartPrefill.primaryCondition ||
              "",
            primaryIcd10:
              existingPayload.primaryIcd10 || chartPrefill.primaryIcd10 || "",
            otherDiagnoses: otherDiagnosesAreEmpty(
              existingPayload.otherDiagnoses,
            )
              ? chartPrefill.otherDiagnoses
              : existingPayload.otherDiagnoses,
          },
        });
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
    // Load once per form identity — unstable callbacks must not retrigger fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  // Auto-calc days from encounter → SOC when both dates are set (editable override).
  useEffect(() => {
    const key = `${formatDateForInput(values.encounterDate) || ""}|${
      formatDateForInput(values.socDate) || ""
    }`;
    const calc = absoluteDayDiff(values.encounterDate, values.socDate);
    const isFirst = prevEncounterSocKey.current === null;
    const datesChanged =
      !isFirst && prevEncounterSocKey.current !== key;
    prevEncounterSocKey.current = key;

    if (!calc) return;
    if (isFirst) {
      setValues((prev) =>
        prev.daysFromEncounterToSoc
          ? prev
          : { ...prev, daysFromEncounterToSoc: calc },
      );
      return;
    }
    if (!datesChanged) return;
    setValues((prev) =>
      prev.daysFromEncounterToSoc === calc
        ? prev
        : { ...prev, daysFromEncounterToSoc: calc },
    );
  }, [values.encounterDate, values.socDate]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateDiagnosisRow = (rowId, patch) => {
    setValues((prev) => ({
      ...prev,
      otherDiagnoses: prev.otherDiagnoses.map((row) =>
        row.id === rowId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateRequestedService = (key, patch) => {
    setValues((prev) => ({
      ...prev,
      requestedServices: {
        ...prev.requestedServices,
        [key]: {
          ...(prev.requestedServices?.[key] || { selected: "", reason: "" }),
          ...patch,
        },
      },
    }));
  };

  const toggleQaChecklist = (item) => {
    setValues((prev) => ({
      ...prev,
      qaChecklist: {
        ...prev.qaChecklist,
        [item]: !prev.qaChecklist?.[item],
      },
    }));
  };

  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const formatOptDate = (v) => (v ? formatDateForInput(v) : "");

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      reviewComments,
      ...clinicalFields
    } = values;

    const nestedPayload = {
      ...clinicalFields,
      dateOfBirth: formatOptDate(values.dateOfBirth),
      socDate: formatOptDate(values.socDate),
      certPeriodFrom: formatOptDate(values.certPeriodFrom),
      certPeriodTo: formatOptDate(values.certPeriodTo),
      encounterDate: formatOptDate(values.encounterDate),
      dateVerified: formatOptDate(values.dateVerified),
      hospitalizationDate: formatOptDate(values.hospitalizationDate),
      attestationEncounterDate: formatOptDate(values.attestationEncounterDate),
      certifyingSignedAt: formatOptDate(values.certifyingSignedAt),
      qaDate: formatOptDate(values.qaDate),
      otherDiagnoses: normalizeDiagnoses(values.otherDiagnoses),
      requestedServices: normalizeRequestedServices(values.requestedServices),
      qaChecklist: normalizeQaChecklist(values.qaChecklist),
      certifyingDifferent: Boolean(values.certifyingDifferent),
    };

    for (const key of F2F_ARRAY_KEYS) {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    }

    const encounterIso = formatOptDate(values.encounterDate) || null;

    const payload = {
      flag,
      orderDate: encounterIso,
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    const sig = signatures.buildSignaturePayload();
    delete sig.patientSignatureImage;
    delete sig.clearPatientSignature;
    return { ...payload, ...sig };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const scheduleSource =
        values.encounterDate || values.orderDate || today();
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateFaceToFaceEncounterForm,
        filledByUserId,
        scheduleDate: scheduleSource
          ? formatDateForInput(scheduleSource)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showTelehealth = values.encounterMethod === "Telehealth";
  const showPlaceOther = (values.placeOfEncounter || []).includes("Other");
  const showMethodOther =
    values.encounterMethod === "Other CMS-permitted method";
  const showCredentialOther = (values.credentials || []).includes("Other");
  const showSymptomOther = (values.symptoms || []).includes("Other");
  const showHomeboundOther = (values.homeboundCriterion1 || []).includes(
    "Other",
  );

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Face-to-Face Encounter Home Health Certification
          </h1>
          <p className="mt-1.5 text-[11px] text-slate-500">
            Medicare Home Health Face-to-Face Encounter Documentation
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <section className="rounded-md border border-slate-800 overflow-hidden">
          <div className="grid grid-cols-5 gap-x-2.5 items-start px-3 py-1.5">
            <HeaderCell
              label="Patient Name"
              value={patientName || form.patientName}
              dense
            />
            <HeaderCell label="MR Number" value={patient?.mrn || values.mrn} dense />
            <HeaderCell
              label="Date of Birth"
              value={displayDateUS(
                patient?.dateOfBirth || values.dateOfBirth || form.patientDateOfBirth,
              )}
              dense
            />
            {caregiverSlot || (
              <HeaderCell
                label="Caregiver Name"
                value={caregiverLabel(form)}
                dense
              />
            )}
            {printMode ? (
              <HeaderCell
                label="Encounter Date"
                value={displayDateUS(values.encounterDate)}
                dense
              />
            ) : (
              <HeaderControlCell label="Encounter Date">
                <InputDatePicker
                  name="encounterDateHeader"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.encounterDate) || ""}
                  onChange={(event) =>
                    setValue("encounterDate", event.target.value)
                  }
                  max={ENCOUNTER_DATE_MAX}
                  className="min-w-0 w-full"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </HeaderControlCell>
            )}
          </div>
        </section>

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        {/* —— 1. Patient Information —— */}
        <Section icon={UsersIcon} title="1. Patient Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Patient Name">
              <input
                type="text"
                className={fieldClass}
                value={values.patientName}
                disabled={!canEdit}
                onChange={(e) => setValue("patientName", e.target.value)}
              />
            </Field>
            <Field label="Date of Birth">
              <DateFieldInput
                name="dateOfBirth"
                value={values.dateOfBirth}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("dateOfBirth", v)}
              />
            </Field>
            <Field label="MRN">
              <input
                type="text"
                className={fieldClass}
                value={values.mrn}
                disabled={!canEdit}
                onChange={(e) => setValue("mrn", e.target.value)}
              />
            </Field>
            <Field label="MBI">
              <input
                type="text"
                className={fieldClass}
                value={values.mbi}
                disabled={!canEdit}
                onChange={(e) => setValue("mbi", e.target.value)}
              />
            </Field>
            <Field label="Phone">
              <input
                type="text"
                className={fieldClass}
                value={values.phone}
                disabled={!canEdit}
                onChange={(e) => setValue("phone", e.target.value)}
              />
            </Field>
            <Field label="Address" span="sm:col-span-2 lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.address}
                disabled={!canEdit}
                onChange={(e) => setValue("address", e.target.value)}
              />
            </Field>
            <Field label="City">
              <input
                type="text"
                className={fieldClass}
                value={values.city}
                disabled={!canEdit}
                onChange={(e) => setValue("city", e.target.value)}
              />
            </Field>
            <Field label="State">
              <select
                className={fieldClass}
                value={values.state}
                disabled={!canEdit}
                onChange={(e) => setValue("state", e.target.value)}
              >
                <option value="">Select…</option>
                {US_STATE_OPTIONS.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="ZIP">
              <input
                type="text"
                className={fieldClass}
                value={values.zip}
                disabled={!canEdit}
                onChange={(e) => setValue("zip", e.target.value)}
              />
            </Field>
            <Field label="Primary Insurance">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryInsurance}
                disabled={!canEdit}
                onChange={(e) => setValue("primaryInsurance", e.target.value)}
              />
            </Field>
            <Field label="Secondary Insurance">
              <input
                type="text"
                className={fieldClass}
                value={values.secondaryInsurance}
                disabled={!canEdit}
                onChange={(e) => setValue("secondaryInsurance", e.target.value)}
              />
            </Field>
            <Field label="Home Health Agency">
              <input
                type="text"
                className={fieldClass}
                value={values.homeHealthAgency}
                disabled={!canEdit}
                onChange={(e) => setValue("homeHealthAgency", e.target.value)}
              />
            </Field>
            <Field label="Agency NPI">
              <input
                type="text"
                className={fieldClass}
                value={values.agencyNpi}
                disabled={!canEdit}
                onChange={(e) => setValue("agencyNpi", e.target.value)}
              />
            </Field>
            <Field label="Agency CCN">
              <input
                type="text"
                className={fieldClass}
                value={values.agencyCcn}
                disabled={!canEdit}
                onChange={(e) => setValue("agencyCcn", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* —— 2. Encounter / Episode —— */}
        <Section icon={CalendarIcon} title="2. Encounter / Episode">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="SOC Date">
              <DateFieldInput
                name="socDate"
                value={values.socDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("socDate", v)}
              />
            </Field>
            <Field label="Cert From">
              <DateFieldInput
                name="certPeriodFrom"
                value={values.certPeriodFrom}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("certPeriodFrom", v)}
              />
            </Field>
            <Field label="Cert To">
              <DateFieldInput
                name="certPeriodTo"
                value={values.certPeriodTo}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("certPeriodTo", v)}
              />
            </Field>
            <Field label="Encounter Date">
              <DateFieldInput
                name="encounterDate"
                value={values.encounterDate}
                canEdit={canEdit}
                printMode={printMode}
                max={ENCOUNTER_DATE_MAX}
                onChange={(v) => setValue("encounterDate", v)}
              />
            </Field>
            <Field label="Days From Encounter to SOC">
              <input
                type="text"
                className={fieldClass}
                value={values.daysFromEncounterToSoc}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("daysFromEncounterToSoc", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Encounter Timing</SubHeading>
            <ExclusiveChoiceGrid
              name="encounterTiming"
              options={ENCOUNTER_TIMING_OPTIONS}
              value={values.encounterTiming || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("encounterTiming", opt)}
              columns={2}
            />
          </div>
          <div className="mt-3">
            <SubHeading>CMS Timeframe Check</SubHeading>
            <ExclusiveChoiceGrid
              name="cmsTimeframeCheck"
              options={CMS_TIMEFRAME_OPTIONS}
              value={values.cmsTimeframeCheck || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("cmsTimeframeCheck", opt)}
              columns={3}
            />
          </div>
        </Section>

        {/* —— 3. Encounter Type —— */}
        <Section icon={MapPinIcon} title="3. Encounter Type">
          <SubHeading>Place of Encounter</SubHeading>
          <CheckboxGrid
            options={PLACE_OF_ENCOUNTER_OPTIONS}
            values={values.placeOfEncounter}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("placeOfEncounter", opt)}
            columns={3}
          />
          {showPlaceOther && (
            <div className="mt-2">
              <Field label="Place of Encounter — Other">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.placeOfEncounterOther}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("placeOfEncounterOther", e.target.value)
                  }
                />
              </Field>
            </div>
          )}

          <div className="mt-3">
            <SubHeading>Encounter Method</SubHeading>
            <ExclusiveChoiceGrid
              name="encounterMethod"
              options={ENCOUNTER_METHOD_OPTIONS}
              value={values.encounterMethod || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("encounterMethod", opt)}
              columns={3}
            />
          </div>
          {showMethodOther && (
            <div className="mt-2">
              <Field label="Encounter Method — Other">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.encounterMethodOther}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("encounterMethodOther", e.target.value)
                  }
                />
              </Field>
            </div>
          )}

          {showTelehealth && (
            <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
              <SubHeading>Telehealth Details</SubHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Platform">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.telehealthPlatform}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("telehealthPlatform", e.target.value)
                    }
                  />
                </Field>
                <Field label="Patient Location">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.telehealthPatientLocation}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("telehealthPatientLocation", e.target.value)
                    }
                  />
                </Field>
                <Field label="Practitioner Location">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.telehealthPractitionerLocation}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("telehealthPractitionerLocation", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-2">
                <SubHeading>Requirements Verified</SubHeading>
                <ExclusiveChoiceGrid
                  name="telehealthRequirementsVerified"
                  options={TELEHEALTH_VERIFIED_OPTIONS}
                  value={values.telehealthRequirementsVerified || ""}
                  canEdit={canEdit}
                  onChange={(opt) =>
                    setValue("telehealthRequirementsVerified", opt)
                  }
                  columns={3}
                />
              </div>
            </div>
          )}
        </Section>

        {/* —— 4. Practitioner —— */}
        <Section icon={DoctorIcon} title="4. Practitioner">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Practitioner Name" span="sm:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.practitionerName}
                disabled={!canEdit}
                onChange={(e) => setValue("practitionerName", e.target.value)}
              />
            </Field>
            <Field label="NPI">
              <input
                type="text"
                className={fieldClass}
                value={values.practitionerNpi}
                disabled={!canEdit}
                onChange={(e) => setValue("practitionerNpi", e.target.value)}
              />
            </Field>
            <Field label="State License">
              <input
                type="text"
                className={fieldClass}
                value={values.stateLicense}
                disabled={!canEdit}
                onChange={(e) => setValue("stateLicense", e.target.value)}
              />
            </Field>
            <Field label="Specialty">
              <input
                type="text"
                className={fieldClass}
                value={values.specialty}
                disabled={!canEdit}
                onChange={(e) => setValue("specialty", e.target.value)}
              />
            </Field>
            <Field label="Organization">
              <input
                type="text"
                className={fieldClass}
                value={values.organization}
                disabled={!canEdit}
                onChange={(e) => setValue("organization", e.target.value)}
              />
            </Field>
            <Field label="Phone">
              <input
                type="text"
                className={fieldClass}
                value={values.practitionerPhone}
                disabled={!canEdit}
                onChange={(e) => setValue("practitionerPhone", e.target.value)}
              />
            </Field>
            <Field label="Fax">
              <input
                type="text"
                className={fieldClass}
                value={values.practitionerFax}
                disabled={!canEdit}
                onChange={(e) => setValue("practitionerFax", e.target.value)}
              />
            </Field>
            <Field label="Verified By">
              <input
                type="text"
                className={fieldClass}
                value={values.verifiedBy}
                disabled={!canEdit}
                onChange={(e) => setValue("verifiedBy", e.target.value)}
              />
            </Field>
            <Field label="Date Verified">
              <DateFieldInput
                name="dateVerified"
                value={values.dateVerified}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("dateVerified", v)}
              />
            </Field>
          </div>

          <div className="mt-3">
            <SubHeading>Practitioner Role</SubHeading>
            <CheckboxGrid
              options={PRACTITIONER_ROLE_OPTIONS}
              values={values.practitionerRoles}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("practitionerRoles", opt)}
              columns={3}
            />
          </div>
          <div className="mt-3">
            <SubHeading>Credentials</SubHeading>
            <CheckboxGrid
              options={CREDENTIAL_OPTIONS}
              values={values.credentials}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("credentials", opt)}
              columns={4}
            />
            {showCredentialOther && (
              <div className="mt-2">
                <Field label="Credentials — Other">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.credentialsOther}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("credentialsOther", e.target.value)
                    }
                  />
                </Field>
              </div>
            )}
          </div>
          <div className="mt-3">
            <SubHeading>Eligibility Checks</SubHeading>
            <CheckboxGrid
              options={ELIGIBILITY_CHECK_OPTIONS}
              values={values.eligibilityChecks}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("eligibilityChecks", opt)}
              columns={2}
            />
          </div>
        </Section>

        {/* —— 5. Primary Reason —— */}
        <Section icon={MedicalNotesIcon} title="5. Primary Reason for Home Health">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Primary Condition / Reason">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryCondition}
                disabled={!canEdit}
                onChange={(e) => setValue("primaryCondition", e.target.value)}
              />
            </Field>
            <Field label="Primary ICD-10">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryIcd10}
                disabled={!canEdit}
                onChange={(e) => setValue("primaryIcd10", e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr
                  className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-2 py-1.5 font-semibold">Other Diagnosis</th>
                  <th className="px-2 py-1.5 font-semibold w-28">ICD-10</th>
                  <th className="px-2 py-1.5 font-semibold w-40">Relevant to HH</th>
                </tr>
              </thead>
              <tbody>
                {(values.otherDiagnoses || []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.diagnosis}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateDiagnosisRow(row.id, {
                            diagnosis: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.icd10}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateDiagnosisRow(row.id, { icd10: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-1">
                      <ExclusiveChoiceGrid
                        name={`dx-relevant-${row.id}`}
                        options={YES_NO_OPTIONS}
                        value={row.relevantToHh || ""}
                        canEdit={canEdit}
                        onChange={(opt) =>
                          updateDiagnosisRow(row.id, { relevantToHh: opt })
                        }
                        columns={2}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3">
            <SubHeading>Encounter Related to Home Health Reason</SubHeading>
            <ExclusiveChoiceGrid
              name="encounterRelatedToHh"
              options={ENCOUNTER_RELATED_OPTIONS}
              value={values.encounterRelatedToHh || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("encounterRelatedToHh", opt)}
              columns={3}
            />
          </div>
          <div className="mt-3">
            <Field label="Clinical Explanation">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.clinicalExplanationReason}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("clinicalExplanationReason", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* —— 6. Clinical Assessment —— */}
        <Section icon={HeartPulseIcon} title="6. Clinical Assessment">
          <Field label="Chief Complaint">
            <textarea
              rows={2}
              className={textareaClass}
              value={values.chiefComplaint}
              disabled={!canEdit}
              onChange={(e) => setValue("chiefComplaint", e.target.value)}
            />
          </Field>
          <div className="mt-3">
            <SubHeading>Symptoms / Findings</SubHeading>
            <CheckboxGrid
              options={SYMPTOM_OPTIONS}
              values={values.symptoms}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("symptoms", opt)}
              columns={3}
            />
            {showSymptomOther && (
              <div className="mt-2">
                <Field label="Symptom — Other">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.symptomOther}
                    disabled={!canEdit}
                    onChange={(e) => setValue("symptomOther", e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
          <div className="mt-3">
            <SubHeading>Vitals</SubHeading>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-3 gap-y-2">
              {[
                ["vitalsTemp", "Temp"],
                ["vitalsPulse", "Pulse"],
                ["vitalsResp", "Resp"],
                ["vitalsBp", "BP"],
                ["vitalsSpo2", "SpO₂"],
                ["vitalsWeight", "Weight"],
                ["vitalsPain", "Pain"],
              ].map(([key, label]) => (
                <Field key={key} label={label}>
                  <input
                    type="text"
                    className={fieldClass}
                    value={values[key]}
                    disabled={!canEdit}
                    onChange={(e) => setValue(key, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </div>
        </Section>

        {/* —— 7. Homebound —— */}
        <Section icon={HomeIcon} title="7. Homebound Status">
          <SubHeading>Criterion 1 — Leaving Home Requires…</SubHeading>
          <CheckboxGrid
            options={HOMEBOUND_CRITERION1_OPTIONS}
            values={values.homeboundCriterion1}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("homeboundCriterion1", opt)}
            columns={2}
          />
          {showHomeboundOther && (
            <div className="mt-2">
              <Field label="Criterion 1 — Other">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.homeboundCriterion1Other}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("homeboundCriterion1Other", e.target.value)
                  }
                />
              </Field>
            </div>
          )}
          <div className="mt-3">
            <Field label="Supporting Clinical Findings">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.homeboundSupportingFindings}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("homeboundSupportingFindings", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Criterion 2 — Normal Inability / Taxing Effort</SubHeading>
            <ExclusiveChoiceGrid
              name="homeboundCriterion2"
              options={YES_NO_OPTIONS}
              value={values.homeboundCriterion2 || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("homeboundCriterion2", opt)}
              columns={2}
            />
          </div>
          <div className="mt-3">
            <Field label="Clinical Explanation (Homebound)">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.homeboundClinicalExplanation}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("homeboundClinicalExplanation", e.target.value)
                }
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Homebound Determination</SubHeading>
            <ExclusiveChoiceGrid
              name="homeboundDetermination"
              options={HOMEBOUND_DETERMINATION_OPTIONS}
              value={values.homeboundDetermination || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("homeboundDetermination", opt)}
              columns={3}
            />
          </div>
        </Section>

        {/* —— 8. Skilled Need —— */}
        <Section icon={ShieldCheckIcon} title="8. Skilled Need">
          <SubHeading>Disciplines</SubHeading>
          <CheckboxGrid
            options={SKILLED_DISCIPLINE_OPTIONS}
            values={values.skilledDisciplines}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("skilledDisciplines", opt)}
            columns={2}
          />
          <div className="mt-3">
            <SubHeading>Skilled Needs</SubHeading>
            <CheckboxGrid
              options={SKILLED_NEED_OPTIONS}
              values={values.skilledNeeds}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("skilledNeeds", opt)}
              columns={3}
            />
          </div>
          <div className="mt-3">
            <Field label="Supporting Findings / Narrative">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.skilledNeedFindings}
                disabled={!canEdit}
                onChange={(e) => setValue("skilledNeedFindings", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* —— 9. Requested Services —— */}
        <Section icon={CheckCircleIcon} title="9. Requested Services">
          <div className="space-y-3">
            {REQUESTED_SERVICE_KEYS.map(({ key, label }) => {
              const row = values.requestedServices?.[key] || {
                selected: "",
                reason: "",
              };
              return (
                <div
                  key={key}
                  className="rounded-md border border-slate-200 px-3 py-2 space-y-2"
                >
                  <div className="text-[12px] font-semibold text-slate-700">
                    {label}
                  </div>
                  <ExclusiveChoiceGrid
                    name={`req-svc-${key}`}
                    options={YES_NO_OPTIONS}
                    value={row.selected || ""}
                    canEdit={canEdit}
                    onChange={(opt) =>
                      updateRequestedService(key, { selected: opt })
                    }
                    columns={2}
                  />
                  <Field label="Reason / Frequency">
                    <input
                      type="text"
                      className={fieldClass}
                      value={row.reason || ""}
                      disabled={!canEdit}
                      onChange={(e) =>
                        updateRequestedService(key, {
                          reason: e.target.value,
                        })
                      }
                    />
                  </Field>
                </div>
              );
            })}
          </div>
        </Section>

        {/* —— 10. Medical Necessity —— */}
        <Section icon={FileCheckIcon} title="10. Medical Necessity Narrative">
          <div className="space-y-2">
            <Field label="Homebound Status Support">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.homeboundStatusSupport}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("homeboundStatusSupport", e.target.value)
                }
              />
            </Field>
            <Field label="Skilled Services Support">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.skilledServicesSupport}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("skilledServicesSupport", e.target.value)
                }
              />
            </Field>
            <Field label="Expected Benefit">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.expectedBenefit}
                disabled={!canEdit}
                onChange={(e) => setValue("expectedBenefit", e.target.value)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <SubHeading>Recent Hospitalization</SubHeading>
            <ExclusiveChoiceGrid
              name="recentHospitalization"
              options={YES_NO_OPTIONS}
              value={values.recentHospitalization || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("recentHospitalization", opt)}
              columns={2}
            />
          </div>
          {values.recentHospitalization === "Yes" && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
              <Field label="Hospitalization Date">
                <DateFieldInput
                  name="hospitalizationDate"
                  value={values.hospitalizationDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("hospitalizationDate", v)}
                />
              </Field>
              <Field label="Facility">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.hospitalizationFacility}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("hospitalizationFacility", e.target.value)
                  }
                />
              </Field>
              <Field label="Reason">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.hospitalizationReason}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("hospitalizationReason", e.target.value)
                  }
                />
              </Field>
            </div>
          )}
          <div className="mt-3">
            <Field label="Diagnostic Tests / Findings">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.diagnosticTests}
                disabled={!canEdit}
                onChange={(e) => setValue("diagnosticTests", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* —— 12. Certifying Practitioner —— */}
        <Section icon={DoctorIcon} title="12. Certifying Practitioner (If Different)">
          <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer mb-3">
            <input
              type="checkbox"
              className={checkboxClass}
              checked={Boolean(values.certifyingDifferent)}
              disabled={!canEdit}
              onChange={() =>
                setValue("certifyingDifferent", !values.certifyingDifferent)
              }
            />
            <span>Certifying practitioner is different</span>
          </label>
          {values.certifyingDifferent && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
              <Field label="Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.certifyingName}
                  disabled={!canEdit}
                  onChange={(e) => setValue("certifyingName", e.target.value)}
                />
              </Field>
              <Field label="Credentials">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.certifyingCredentials}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("certifyingCredentials", e.target.value)
                  }
                />
              </Field>
              <Field label="NPI">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.certifyingNpi}
                  disabled={!canEdit}
                  onChange={(e) => setValue("certifyingNpi", e.target.value)}
                />
              </Field>
              <Field label="Organization">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.certifyingOrganization}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("certifyingOrganization", e.target.value)
                  }
                />
              </Field>
              <Field label="Phone">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.certifyingPhone}
                  disabled={!canEdit}
                  onChange={(e) => setValue("certifyingPhone", e.target.value)}
                />
              </Field>
              <Field label="Signed At">
                <DateFieldInput
                  name="certifyingSignedAt"
                  value={values.certifyingSignedAt}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("certifyingSignedAt", v)}
                />
              </Field>
              <Field label="Address" span="sm:col-span-2 lg:col-span-3">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.certifyingAddress}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("certifyingAddress", e.target.value)
                  }
                />
              </Field>
            </div>
          )}
        </Section>

        {/* —— 13. QA Review (overview) —— */}
        <Section
          icon={FileCheckIcon}
          title="13. QA Review (Office Use)"
          className="bg-slate-50/80"
        >
          <SubHeading>Checklist</SubHeading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
            {QA_CHECKLIST_OPTIONS.map((item) => (
              <label
                key={item}
                className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer rounded-md border border-slate-200 bg-white px-2.5 py-2"
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={Boolean(values.qaChecklist?.[item])}
                  disabled={!canEdit}
                  onChange={() => toggleQaChecklist(item)}
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="QA Status">
              <select
                className={fieldClass}
                value={values.qaStatus}
                disabled={!canEdit}
                onChange={(e) => setValue("qaStatus", e.target.value)}
              >
                <option value="">Select…</option>
                {QA_STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Reviewer">
              <input
                type="text"
                className={fieldClass}
                value={values.qaReviewer}
                disabled={!canEdit}
                onChange={(e) => setValue("qaReviewer", e.target.value)}
              />
            </Field>
            <Field label="QA Date">
              <DateFieldInput
                name="qaDate"
                value={values.qaDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("qaDate", v)}
              />
            </Field>
            <Field label="QA Comments" span="sm:col-span-2 lg:col-span-3">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.qaComments}
                disabled={!canEdit}
                onChange={(e) => setValue("qaComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* Clinician Authentication — directly above Office Review (SPO pattern) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2">
          <Field label="Encounter Date">
            <DateFieldInput
              name="attestationEncounterDate"
              value={values.attestationEncounterDate}
              canEdit={canEdit}
              printMode={printMode}
              onChange={(v) => setValue("attestationEncounterDate", v)}
            />
          </Field>
          <Field label="Practitioner Name">
            <input
              type="text"
              className={fieldClass}
              value={values.attestationPractitionerName}
              disabled={!canEdit}
              onChange={(e) =>
                setValue("attestationPractitionerName", e.target.value)
              }
            />
          </Field>
          <Field label="Credentials">
            <input
              type="text"
              className={fieldClass}
              value={values.attestationCredentials}
              disabled={!canEdit}
              onChange={(e) =>
                setValue("attestationCredentials", e.target.value)
              }
            />
          </Field>
          <Field label="NPI">
            <input
              type="text"
              className={fieldClass}
              value={values.attestationNpi}
              disabled={!canEdit}
              onChange={(e) => setValue("attestationNpi", e.target.value)}
            />
          </Field>
        </div>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

FaceToFaceEncounterForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default FaceToFaceEncounterForm;
